import { Badge, Button, type ContextMenuItem, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plug } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  listSpaceConnectorsOptions,
  listSpaceConnectorsQueryKey,
  removeSpaceConnectorMutation,
  type SpaceConnectorResponse,
} from "@/api";
import { EntityRow } from "@/components/build/EntityRow";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { ConnectorIcon } from "@/features/build/connectors/ConnectorIcon";
import { connectorName } from "@/features/build/connectors/connectorName";
import { useSettingsSpace } from "../useSpaceSettings";
import { AddSpaceConnectorModal } from "./AddSpaceConnectorModal";
import { SpaceConnectorModal } from "./SpaceConnectorModal";

/** "Project: atlas, Region: eu · 2 skills" for a row. */
function useSummary() {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.connectors" });
  return (binding: SpaceConnectorResponse): string => {
    const values = (binding.settings ?? [])
      .map((s) => {
        const value = binding.config?.[s.name] || s.default;
        return value ? `${s.label}: ${value}` : null;
      })
      .filter(Boolean);
    const skills = (binding.skills ?? []).length;
    return [values.join(", ") || t("noSettings"), skills > 0 && t("skillCount", { count: skills })]
      .filter(Boolean)
      .join(" · ");
  };
}

function StatusBadge({ binding }: Readonly<{ binding: SpaceConnectorResponse }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.connectors" });
  if (binding.connector.disabledByAdmin) return <Badge variant="danger">{t("disabled")}</Badge>;
  if (!binding.connector.isEnabled) return <Badge>{t("off")}</Badge>;
  const missing = binding.membersWithoutToken ?? 0;
  if (missing > 0) return <Badge variant="warning">{t("missing", { count: missing })}</Badge>;
  return null;
}

/** Space settings > Connectors: what this space's members can use while chatting in it (phase 4). */
export function SpaceConnectorsSection() {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.connectors" });
  const { t: tc } = useTranslation("connectors");
  const space = useSettingsSpace();
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = { space_uuid: space.uuid };
  const bindings = useQuery({ ...listSpaceConnectorsOptions({ path }), select: (r) => r.data });
  const summary = useSummary();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<SpaceConnectorResponse | null>(null);
  const closeAdd = useCallback(() => setAdding(false), []);
  const closeEdit = useCallback(() => setEditing(null), []);
  const remove = useMutation({
    ...removeSpaceConnectorMutation(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: listSpaceConnectorsQueryKey({ path }) }),
    onError: (error) => toast.error(errorMessage(error, t("failed"))),
  });

  const menu = (b: SpaceConnectorResponse): ContextMenuItem[] => [
    { label: t("edit"), onSelect: () => setEditing(b) },
    {
      label: t("remove"),
      danger: true,
      onSelect: () =>
        remove.mutate(
          { path: { ...path, binding_uuid: b.uuid } },
          { onSuccess: () => toast.success(t("removed", { name: connectorName(b.connector, tc) })) },
        ),
    },
  ];

  const list = bindings.data?.connectors ?? [];
  return (
    <Page title={t("title")}>
      <div className="flex items-start justify-between gap-3">
        <Text variant="body-sm" tone="secondary" className="max-w-xl">
          {t("hint")}
        </Text>
        <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
          {t("add")}
        </Button>
      </div>
      <QuerySection
        query={bindings}
        isEmpty={(data) => (data.connectors ?? []).length === 0}
        empty={{ icon: <Plug size={18} />, hint: t("empty") }}
      >
        {() => (
          <ul aria-label={t("listLabel")} className="flex flex-col gap-2">
            {list.map((b) => (
              <li key={b.uuid}>
                <EntityRow
                  icon={<ConnectorIcon transport={b.connector.transport} />}
                  name={connectorName(b.connector, tc)}
                  description={summary(b)}
                  meta={<StatusBadge binding={b} />}
                  onOpen={() => setEditing(b)}
                  menuItems={menu(b)}
                />
              </li>
            ))}
          </ul>
        )}
      </QuerySection>
      {adding && (
        <AddSpaceConnectorModal
          spaceUuid={space.uuid}
          existing={list.map((b) => b.connector.uuid)}
          onClose={closeAdd}
        />
      )}
      {editing && <SpaceConnectorModal spaceUuid={space.uuid} binding={editing} onClose={closeEdit} />}
    </Page>
  );
}
